import { createSlice } from '@reduxjs/toolkit';
import axios from '../../config/axios-config';
import { setApiLoading, setApiSuccess, setApiError, ApiStatusKeys } from './apiStatusSlice';
import { handleApiError } from '../../helpers/errorHelper';

const initialState = {
  dashboardStats: {
    totalUsers: 0,
    totalCoaches: 0,
    totalMembers: 0,
    enabledUsers: 0,
    disabledUsers: 0,
    activeUsersRatio: 0.0,
    coachesRatio: 0.0,
    membersRatio: 0.0,
  },
  users: [],
  selectedUser: null,
  selectedCategory: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setDashboardStats: (state, action) => {
      state.dashboardStats = action.payload;
    },
    clearDashboardStats: (state) => {
      state.dashboardStats = {
        totalUsers: 0,
        totalCoaches: 0,
        totalMembers: 0,
        enabledUsers: 0,
        disabledUsers: 0,
        activeUsersRatio: 0.0,
        coachesRatio: 0.0,
        membersRatio: 0.0,
      };
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    clearUsers: (state) => {
      state.users = [];
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    },
  },
});

export const {
  setDashboardStats,
  clearDashboardStats,
  setUsers,
  clearUsers,
  setSelectedUser,
  clearSelectedUser,
  setSelectedCategory,
  clearSelectedCategory,
} = adminSlice.actions;

// Thunk action creators
export const getDashboardStats = () => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_GET_DASHBOARD_STATS));

    const response = await axios.get('/admin/dashboard-stats');

    if (response.data.success) {
      dispatch(setDashboardStats(response.data.dashboardStats));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_GET_DASHBOARD_STATS));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_GET_DASHBOARD_STATS, dispatch, 'errors.dashboard_stats_failed');
    throw error;
  }
};

export const getUsers = (searchParams) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_GET_USERS));

    var request = {
      SearchType: searchParams.type,
      Email: searchParams.query,
    };

    const response = await axios.post('/admin/user/search', request);

    if (response.data.success) {
      dispatch(setUsers(response.data.users || []));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_GET_USERS));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_GET_USERS, dispatch, 'errors.user_failed');
    throw error;
  }
};

export const getLastUsers = () => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_GET_LAST_USERS));

    const response = await axios.get('/admin/user/last');

    if (response.data.success) {
      dispatch(setUsers(response.data.users || []));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_GET_LAST_USERS));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_GET_LAST_USERS, dispatch, 'errors.user_failed');
    throw error;
  }
};

export const deleteUser = (userId) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_DELETE_USER));

    const response = await axios.delete('/admin/user/delete', {
      data: { id: userId },
    });

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_DELETE_USER));
      return true;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_DELETE_USER, dispatch, 'errors.user_failed');
    throw error;
  }
};

export const getUserById = (userId) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_GET_USER_BY_ID));

    const response = await axios.get(`/admin/user/get-by-id?Id=${userId}`);

    if (response.data.success) {
      dispatch(setSelectedUser(response.data.user));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_GET_USER_BY_ID));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_GET_USER_BY_ID, dispatch, 'errors.user_failed');
    throw error;
  }
};

export const updateUser = (selectedUser) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_UPDATE_USER));

    const response = await axios.post(`/admin/user/update`, { user: selectedUser });

    if (response.data.success) {
      dispatch(setSelectedUser(response.data.user));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_UPDATE_USER));
      return true;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_UPDATE_USER, dispatch, 'errors.user_update_failed');
    throw error;
  }
};

export const createCategory = (newCategory) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_CREATE_CATEGORY));

    const response = await axios.post('/admin/category/create', {
      category: newCategory,
    });

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_CREATE_CATEGORY));
      return response.data;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_CREATE_CATEGORY, dispatch, 'errors.category_create_failed');
    throw error;
  }
};

export const updateCategory = (selectedCategory) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_UPDATE_CATEGORY));

    const response = await axios.post('/admin/category/update', {
      category: selectedCategory,
    });

    if (response.data.success) {
      dispatch(setSelectedCategory(response.data.category));
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_UPDATE_CATEGORY));
      return response.data;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_UPDATE_CATEGORY, dispatch, 'errors.category_update_failed');
    throw error;
  }
};

export const deleteCategory = (categoryId) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_DELETE_CATEGORY));

    const response = await axios.delete('/admin/category/delete', {
      data: { id: categoryId },
    });

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_DELETE_CATEGORY));
      return true;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.ADMIN_DELETE_CATEGORY, dispatch, 'errors.category_delete_failed');
    throw error;
  }
};

export const setEmbeddingsCategory = () => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ADMIN_SET_EMBEDDINGS_CATEGORY));

    const response = await axios.get('/admin/category/embedded-categories');

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.ADMIN_SET_EMBEDDINGS_CATEGORY));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(
      error,
      ApiStatusKeys.ADMIN_SET_EMBEDDINGS_CATEGORY,
      dispatch,
      'errors.set_embeddings_category_failed',
    );
    throw error;
  }
};

export default adminSlice.reducer;
